<script setup>
import { ref, reactive, computed } from "vue";
import { apiQuery } from "@/api/j24api";
import { fetchAuthSession } from "aws-amplify/auth";

const config = reactive({
  url: "",
  username: "",
  password: "",
});

const currentStep = ref(1);
const loadingButton = ref(false);
const loadingReport = ref(false);
const showFailedCredentialDialog = ref(false);
const showWarningDialog = ref(false);

const reportOptions = reactive({
  computerProfiles: false,
  mobileProfiles: false,
  computerGroups: false,
  mobileGroups: false,
  computerPrestages: false,
  mobilePrestages: false,
  comparison: false,
  securityAudit: false,
});

// Report data
const reportResults = reactive({
  computerProfiles: null,
  mobileProfiles: null,
  computerGroups: null,
  mobileGroups: null,
  computerPrestages: null,
  mobilePrestages: null,
  comparison: null,
  securityAudit: null,
});

const reportProgress = reactive({
  current: "",
  completed: [],
  total: 0,
});

const activeTab = ref(null);
const reportGenerated = ref(false);

const steps = [
  { title: "Jamf Credentials", value: 1, complete: false },
  { title: "Report Options", value: 2, complete: false },
  { title: "Results", value: 3, complete: false },
];

const hasSelectedOption = computed(() =>
  Object.values(reportOptions).some((v) => v === true)
);

const completedTabs = computed(() => {
  const tabs = [];
  if (reportResults.computerProfiles)
    tabs.push({ key: "computerProfiles", label: "macOS Profiles" });
  if (reportResults.mobileProfiles)
    tabs.push({ key: "mobileProfiles", label: "iOS Profiles" });
  if (reportResults.computerGroups)
    tabs.push({ key: "computerGroups", label: "Computer Groups" });
  if (reportResults.mobileGroups)
    tabs.push({ key: "mobileGroups", label: "Mobile Groups" });
  if (reportResults.computerPrestages)
    tabs.push({ key: "computerPrestages", label: "Computer Prestages" });
  if (reportResults.mobilePrestages)
    tabs.push({ key: "mobilePrestages", label: "Mobile Prestages" });
  if (reportResults.comparison)
    tabs.push({ key: "comparison", label: "Deployment Comparison" });
  if (reportResults.securityAudit)
    tabs.push({ key: "securityAudit", label: "Security Audit" });
  return tabs;
});

async function getPortalUser() {
  try {
    const session = await fetchAuthSession();
    return session.tokens.idToken.payload.email;
  } catch {
    return "Unknown";
  }
}

async function verifyCredentials() {
  loadingButton.value = true;
  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "credentialsCheck",
    config: {
      url: config.url.replace(/\/$/, ""),
      username: config.username,
      password: config.password,
    },
  });

  if (result === "Success") {
    nextStep();
  } else {
    showFailedCredentialDialog.value = true;
  }
  loadingButton.value = false;
}

function nextStep() {
  if (currentStep.value < steps.length) {
    if (currentStep.value === 2) {
      if (!hasSelectedOption.value) {
        showWarningDialog.value = true;
        return;
      }
    }

    steps[currentStep.value - 1].complete = true;
    currentStep.value++;

    if (currentStep.value === 3) {
      generateReports();
    }
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    steps[currentStep.value - 1].complete = false;
  }
}

async function generateReports() {
  loadingReport.value = true;
  reportGenerated.value = false;

  const configPayload = {
    url: config.url.replace(/\/$/, ""),
    username: config.username,
    password: config.password,
  };

  // Build list of reports to run
  const tasks = [];
  if (reportOptions.securityAudit) {
    tasks.push({
      key: "securityAudit",
      label: "Security Audit",
      command: "configReportSecurityAudit",
      params: { config: configPayload, email: await getPortalUser() },
    });
  }
  if (reportOptions.computerProfiles) {
    tasks.push({
      key: "computerProfiles",
      label: "macOS Configuration Profiles",
      command: "configReportProfiles",
      params: { config: configPayload, deviceType: "computer" },
    });
  }
  if (reportOptions.mobileProfiles) {
    tasks.push({
      key: "mobileProfiles",
      label: "iOS Configuration Profiles",
      command: "configReportProfiles",
      params: { config: configPayload, deviceType: "mobile" },
    });
  }
  if (reportOptions.computerGroups) {
    tasks.push({
      key: "computerGroups",
      label: "Computer Groups",
      command: "configReportGroups",
      params: { config: configPayload, deviceType: "computer" },
    });
  }
  if (reportOptions.mobileGroups) {
    tasks.push({
      key: "mobileGroups",
      label: "Mobile Device Groups",
      command: "configReportGroups",
      params: { config: configPayload, deviceType: "mobile" },
    });
  }
  if (reportOptions.computerPrestages) {
    tasks.push({
      key: "computerPrestages",
      label: "Computer Prestage Assignments",
      command: "configReportPrestages",
      params: { config: configPayload, deviceType: "computer" },
    });
  }
  if (reportOptions.mobilePrestages) {
    tasks.push({
      key: "mobilePrestages",
      label: "Mobile Prestage Assignments",
      command: "configReportPrestages",
      params: { config: configPayload, deviceType: "mobile" },
    });
  }
  if (reportOptions.comparison) {
    tasks.push({
      key: "comparison",
      label: "Deployment Comparison",
      command: "configReportComparison",
      params: { config: configPayload },
    });
  }

  reportProgress.total = tasks.length;
  reportProgress.completed = [];

  for (const task of tasks) {
    reportProgress.current = task.label;
    try {
      const result = await apiQuery("ELA", "mdmTools/command", {
        mdm: "jamf",
        command: task.command,
        ...task.params,
      });
      reportResults[task.key] = result;
    } catch (err) {
      reportResults[task.key] = { error: err.message || "Request failed" };
    }
    reportProgress.completed.push(task.key);
  }

  reportProgress.current = "";
  loadingReport.value = false;
  reportGenerated.value = true;

  // Set active tab to first completed tab
  if (completedTabs.value.length > 0) {
    activeTab.value = completedTabs.value[0].key;
  }

  steps[2].complete = true;
}

function formatSettingValue(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function downloadCSV(prestageData) {
  if (!prestageData || !prestageData.csvData) return;
  let csv = "Serial,Prestage\n";
  for (const row of prestageData.csvData) {
    csv += `${row.serial},${row.prestage}\n`;
  }
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "Prestage Report.csv";
  link.click();
  URL.revokeObjectURL(link.href);
}

function buildProfileHtml(data, title) {
  if (!data || data.error) return "";
  let html = `<h2>${title}</h2>`;

  // Profile details
  for (const profile of data.profiles || []) {
    html += `<h3>${profile.name}</h3>`;
    if (profile.description) {
      html += `<p><em>${profile.description}</em></p>`;
    }
    if (profile.scopeNames && profile.scopeNames.length > 0) {
      html += `<p><strong>Scope:</strong> ${profile.scopeNames.join(", ")}</p>`;
    }
    if (profile.payloads && profile.payloads.length > 0) {
      for (const payload of profile.payloads) {
        html += `<h4>${payload.payloadName}</h4>`;
        html += `<table><thead><tr><th>Setting</th><th>Value</th></tr></thead><tbody>`;
        for (const setting of payload.settings || []) {
          html += `<tr><td>${setting.key}</td><td>${formatSettingValue(
            setting.value
          )}</td></tr>`;
        }
        html += `</tbody></table>`;
      }
    } else {
      html += `<p>No parseable payload settings found.</p>`;
    }
  }

  // Scope cross reference
  if (data.scopeReport && Object.keys(data.scopeReport).length > 0) {
    html += `<h3>Scope Cross Reference</h3>`;
    html += `<table><thead><tr><th>Scope Target</th><th>Profiles</th></tr></thead><tbody>`;
    for (const [scopeName, profiles] of Object.entries(data.scopeReport)) {
      html += `<tr><td><strong>${scopeName}</strong></td><td>${profiles.join(
        ", "
      )}</td></tr>`;
    }
    html += `</tbody></table>`;
  }

  return html;
}

function buildGroupHtml(data, title) {
  if (!data || data.error) return "";
  let html = `<h2>${title}</h2>`;
  html += `<table><thead><tr><th>Name</th><th>Type</th><th>Members</th><th>Criteria</th></tr></thead><tbody>`;
  for (const group of data.groups || []) {
    html += `<tr><td>${group.name}</td><td>${group.type}</td><td>${
      group.memberCount
    }</td><td>${group.criteriaString || ""}</td></tr>`;
  }
  html += `</tbody></table>`;
  return html;
}

function buildPrestageHtml(data, title) {
  if (!data || data.error) return "";
  let html = `<h2>${title}</h2>`;
  for (const [prestageName, serials] of Object.entries(data.prestages || {})) {
    html += `<h3>${prestageName} (${serials.length} devices)</h3>`;
    html += `<p>${serials.join(", ")}</p>`;
  }
  return html;
}

function buildComparisonHtml(data) {
  if (!data || data.error || data.message) return "";
  let html = `<h2>Deployment Comparison</h2>`;
  html += `<table><thead><tr><th>Template Name</th><th>Type</th><th>Status</th></tr></thead><tbody>`;
  for (const item of data.comparisons || []) {
    html += `<tr><td>${item.name}</td><td>${item.type}</td><td>${item.status}</td></tr>`;
    if (item.changes) {
      html += `<tr><td colspan="3"><table><thead><tr><th>Field</th><th>Current (Jamf)</th><th>Template (GitHub)</th></tr></thead><tbody>`;
      for (const [field, diff] of Object.entries(item.changes)) {
        html += `<tr><td>${field}</td><td>${formatSettingValue(
          diff.current
        )}</td><td>${formatSettingValue(diff.template)}</td></tr>`;
      }
      html += `</tbody></table></td></tr>`;
    }
  }
  html += `</tbody></table>`;
  return html;
}

function downloadWord() {
  let content = "";

  if (reportResults.computerProfiles) {
    content += buildProfileHtml(
      reportResults.computerProfiles,
      "macOS Configuration Profiles"
    );
  }
  if (reportResults.mobileProfiles) {
    content += buildProfileHtml(
      reportResults.mobileProfiles,
      "iOS Configuration Profiles"
    );
  }
  if (reportResults.computerGroups) {
    content += buildGroupHtml(reportResults.computerGroups, "Computer Groups");
  }
  if (reportResults.mobileGroups) {
    content += buildGroupHtml(
      reportResults.mobileGroups,
      "Mobile Device Groups"
    );
  }
  if (reportResults.computerPrestages) {
    content += buildPrestageHtml(
      reportResults.computerPrestages,
      "Computer Prestage Assignments"
    );
  }
  if (reportResults.mobilePrestages) {
    content += buildPrestageHtml(
      reportResults.mobilePrestages,
      "Mobile Prestage Assignments"
    );
  }
  if (reportResults.comparison) {
    content += buildComparisonHtml(reportResults.comparison);
  }

  if (!content) return;

  const css = `
    <style>
      @page WordSection { size: 841.95pt 595.35pt; mso-page-orientation: landscape; }
      div.WordSection { page: WordSection; }
      table { border-collapse: collapse; width: 100%; margin-bottom: 12px; }
      th, td { border: 1px solid gray; padding: 4px 8px; font-size: 11px; }
      th { background-color: #f0f0f0; font-weight: bold; }
      h2 { margin-top: 24px; }
      h3 { margin-top: 16px; }
      h4 { margin-top: 8px; margin-bottom: 4px; }
      pre { font-family: monospace; font-size: 10px; white-space: pre-wrap; }
    </style>
  `;

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:w="urn:schemas-microsoft-com:office:word"
          xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="utf-8">${css}</head>
    <body><div class="WordSection"><h1>Jamf Configuration Report</h1><p>${config.url}</p>${content}</div></body>
    </html>
  `;

  const blob = new Blob([html], { type: "application/msword" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "Jamf Config Report.doc";
  link.click();
  URL.revokeObjectURL(link.href);
}

const groupHeaders = [
  { title: "Name", value: "name", sortable: true },
  { title: "Type", value: "type", sortable: true, width: "15%" },
  { title: "Members", value: "memberCount", sortable: true, width: "10%" },
  { title: "Criteria", value: "criteriaString", sortable: false },
];

const comparisonHeaders = [
  { title: "Template Name", value: "name", sortable: true },
  { title: "Type", value: "type", sortable: true, width: "20%" },
  { title: "Status", value: "status", sortable: true, width: "15%" },
];

function comparisonStatusColor(status) {
  switch (status) {
    case "Match":
      return "green";
    case "Changed":
      return "orange";
    case "Not Found in Jamf":
      return "red";
    case "Error":
      return "red";
    case "Skipped":
      return "grey";
    default:
      return "grey";
  }
}
</script>

<template>
  <v-stepper v-model="currentStep">
    <v-alert
      v-if="currentStep > 1"
      type="info"
      variant="tonal"
      density="compact"
      class="mx-4 mt-3"
    >
      <span class="font-weight-bold">Connected to:</span> {{ config.url }}
    </v-alert>
    <v-stepper-header>
      <v-stepper-item
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :value="step.value"
        :complete="step.complete"
      ></v-stepper-item>
    </v-stepper-header>
    <v-stepper-content
      v-for="(step, index) in steps"
      :key="index"
      :step="step.value"
    >
      <v-sheet v-if="step.value === currentStep">
        <v-card-text>
          <!-- Step 1: Jamf Credentials -->
          <template v-if="step.value === 1">
            <v-card max-width="500" variant="elevated" class="mx-auto">
              <v-card-text>
                <v-table class="text-left">
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.url"
                      label="Target Jamf Pro URL"
                      type="input"
                      hint="e.g. https://yourinstance.jamfcloud.com"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.username"
                      label="API Username"
                      type="input"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.password"
                      label="API Password"
                      type="password"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-btn
                    block
                    class="text-none mb-4"
                    size="x-large"
                    @click="verifyCredentials"
                    :disabled="
                      !config.url ||
                      !config.username ||
                      !config.password ||
                      loadingButton
                    "
                    :loading="loadingButton"
                    variant="tonal"
                    color="darkgrey"
                    text="Validate Credentials"
                  ></v-btn>
                </v-table>
              </v-card-text>
            </v-card>
          </template>

          <!-- Step 2: Report Options -->
          <template v-if="step.value === 2">
            <h2>Select report types to generate</h2>
            <v-row class="mt-2">
              <v-col cols="12" md="4">
                <v-card variant="elevated">
                  <v-card-title>macOS Configuration</v-card-title>
                  <v-card-text>
                    <v-checkbox
                      v-model="reportOptions.computerProfiles"
                      label="Configuration Profiles"
                      hide-details
                      density="compact"
                    ></v-checkbox>
                    <v-checkbox
                      v-model="reportOptions.computerGroups"
                      label="Computer Groups"
                      hide-details
                      density="compact"
                    ></v-checkbox>
                    <v-checkbox
                      v-model="reportOptions.computerPrestages"
                      label="Prestage Assignments"
                      hide-details
                      density="compact"
                    ></v-checkbox>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card variant="elevated">
                  <v-card-title>Mobile Device Configuration</v-card-title>
                  <v-card-text>
                    <v-checkbox
                      v-model="reportOptions.mobileProfiles"
                      label="Configuration Profiles"
                      hide-details
                      density="compact"
                    ></v-checkbox>
                    <v-checkbox
                      v-model="reportOptions.mobileGroups"
                      label="Device Groups"
                      hide-details
                      density="compact"
                    ></v-checkbox>
                    <v-checkbox
                      v-model="reportOptions.mobilePrestages"
                      label="Prestage Assignments"
                      hide-details
                      density="compact"
                    ></v-checkbox>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card variant="elevated">
                  <v-card-title>Global</v-card-title>
                  <v-card-text>
                    <v-checkbox
                      v-model="reportOptions.comparison"
                      label="Deployment Comparison"
                      hint="Compares deployed templates against current Jamf state"
                      persistent-hint
                      density="compact"
                    ></v-checkbox>
                    <v-checkbox
                      v-model="reportOptions.securityAudit"
                      label="Security Audit"
                      hint="Report will be delivered via email"
                      persistent-hint
                      density="compact"
                    ></v-checkbox>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <!-- Step 3: Results -->
          <template v-if="step.value === 3">
            <!-- Loading State -->
            <div v-if="loadingReport" class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
              <div class="text-h6 mt-4">Generating reports...</div>
              <div class="text-body-1 mt-2">
                {{ reportProgress.current }}
              </div>
              <div class="text-body-2 text-medium-emphasis mt-1">
                {{ reportProgress.completed.length }} /
                {{ reportProgress.total }} complete
              </div>
              <v-progress-linear
                :model-value="
                  (reportProgress.completed.length / reportProgress.total) * 100
                "
                color="primary"
                class="mt-4 mx-auto"
                style="max-width: 400px"
              ></v-progress-linear>
            </div>

            <!-- Report Results -->
            <template v-if="reportGenerated">
              <div class="d-flex align-center mb-3">
                <h2>Report Results</h2>
                <v-spacer />
                <v-btn
                  variant="tonal"
                  size="small"
                  class="mr-2"
                  @click="downloadWord"
                >
                  <v-icon start>mdi-file-word-outline</v-icon>
                  Download Word
                </v-btn>
              </div>

              <v-tabs v-model="activeTab" color="primary">
                <v-tab
                  v-for="tab in completedTabs"
                  :key="tab.key"
                  :value="tab.key"
                >
                  {{ tab.label }}
                </v-tab>
              </v-tabs>

              <div id="report-content">
                <v-tabs-window v-model="activeTab">
                  <!-- Profile Tabs -->
                  <v-tabs-window-item
                    v-for="profileKey in ['computerProfiles', 'mobileProfiles']"
                    :key="profileKey"
                    :value="profileKey"
                  >
                    <template v-if="reportResults[profileKey]">
                      <v-alert
                        v-if="reportResults[profileKey].error"
                        type="error"
                        variant="tonal"
                        class="mt-3"
                      >
                        {{ reportResults[profileKey].error }}
                      </v-alert>
                      <template v-else>
                        <v-expansion-panels class="mt-3" variant="accordion">
                          <v-expansion-panel
                            v-for="profile in reportResults[profileKey]
                              .profiles"
                            :key="profile.id"
                          >
                            <v-expansion-panel-title>
                              <div>
                                <strong>{{ profile.name }}</strong>
                                <span
                                  v-if="profile.description"
                                  class="text-medium-emphasis ml-2"
                                >
                                  - {{ profile.description }}
                                </span>
                              </div>
                              <template #actions>
                                <div class="d-flex ga-1">
                                  <v-chip
                                    v-for="scope in profile.scopeNames.slice(
                                      0,
                                      3
                                    )"
                                    :key="scope"
                                    size="x-small"
                                    color="blue"
                                    variant="tonal"
                                  >
                                    {{ scope }}
                                  </v-chip>
                                  <v-chip
                                    v-if="profile.scopeNames.length > 3"
                                    size="x-small"
                                    color="grey"
                                    variant="tonal"
                                  >
                                    +{{ profile.scopeNames.length - 3 }} more
                                  </v-chip>
                                </div>
                              </template>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                              <div
                                v-for="payload in profile.payloads"
                                :key="payload.payloadName"
                                class="mb-4"
                              >
                                <div class="font-weight-bold mb-1">
                                  {{ payload.payloadName }}
                                </div>
                                <v-table density="compact">
                                  <thead>
                                    <tr>
                                      <th>Setting</th>
                                      <th>Value</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr
                                      v-for="(
                                        setting, sIdx
                                      ) in payload.settings"
                                      :key="sIdx"
                                    >
                                      <td style="width: 40%">
                                        {{ setting.key }}
                                      </td>
                                      <td>
                                        <pre
                                          v-if="
                                            typeof setting.value === 'object'
                                          "
                                          class="setting-value"
                                          >{{
                                            formatSettingValue(setting.value)
                                          }}</pre
                                        >
                                        <span v-else>{{
                                          formatSettingValue(setting.value)
                                        }}</span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </v-table>
                              </div>
                              <div
                                v-if="
                                  !profile.payloads ||
                                  profile.payloads.length === 0
                                "
                                class="text-medium-emphasis"
                              >
                                No parseable payload settings found.
                              </div>
                            </v-expansion-panel-text>
                          </v-expansion-panel>
                        </v-expansion-panels>

                        <!-- Scope Cross Reference -->
                        <div
                          v-if="
                            reportResults[profileKey].scopeReport &&
                            Object.keys(reportResults[profileKey].scopeReport)
                              .length > 0
                          "
                          class="mt-6"
                        >
                          <h3>Scope Cross Reference</h3>
                          <v-table density="compact" class="mt-2">
                            <thead>
                              <tr>
                                <th>Scope Target</th>
                                <th>Profiles</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="(profiles, scopeName) in reportResults[
                                  profileKey
                                ].scopeReport"
                                :key="scopeName"
                              >
                                <td style="width: 30%">
                                  <strong>{{ scopeName }}</strong>
                                </td>
                                <td>{{ profiles.join(", ") }}</td>
                              </tr>
                            </tbody>
                          </v-table>
                        </div>
                      </template>
                    </template>
                  </v-tabs-window-item>

                  <!-- Group Tabs -->
                  <v-tabs-window-item
                    v-for="groupKey in ['computerGroups', 'mobileGroups']"
                    :key="groupKey"
                    :value="groupKey"
                  >
                    <template v-if="reportResults[groupKey]">
                      <v-alert
                        v-if="reportResults[groupKey].error"
                        type="error"
                        variant="tonal"
                        class="mt-3"
                      >
                        {{ reportResults[groupKey].error }}
                      </v-alert>
                      <v-data-table
                        v-else
                        :headers="groupHeaders"
                        :items="reportResults[groupKey].groups || []"
                        :items-per-page="25"
                        class="elevation-2 mt-3"
                      >
                        <template #[`item.type`]="{ item }">
                          <v-chip
                            :color="
                              item.type === 'Smart Group' ? 'blue' : 'grey'
                            "
                            size="small"
                            variant="tonal"
                          >
                            {{ item.type }}
                          </v-chip>
                        </template>
                        <template #[`item.criteriaString`]="{ item }">
                          <span class="text-body-2">{{
                            item.criteriaString
                          }}</span>
                        </template>
                      </v-data-table>
                    </template>
                  </v-tabs-window-item>

                  <!-- Prestage Tabs -->
                  <v-tabs-window-item
                    v-for="prestageKey in [
                      'computerPrestages',
                      'mobilePrestages',
                    ]"
                    :key="prestageKey"
                    :value="prestageKey"
                  >
                    <template v-if="reportResults[prestageKey]">
                      <v-alert
                        v-if="reportResults[prestageKey].error"
                        type="error"
                        variant="tonal"
                        class="mt-3"
                      >
                        {{ reportResults[prestageKey].error }}
                      </v-alert>
                      <template v-else>
                        <div class="d-flex justify-end mt-3 mb-2">
                          <v-btn
                            variant="tonal"
                            size="small"
                            @click="downloadCSV(reportResults[prestageKey])"
                          >
                            <v-icon start>mdi-download</v-icon>
                            Download CSV
                          </v-btn>
                        </div>
                        <v-expansion-panels variant="accordion">
                          <v-expansion-panel
                            v-for="(serials, prestageName) in reportResults[
                              prestageKey
                            ].prestages"
                            :key="prestageName"
                          >
                            <v-expansion-panel-title>
                              <strong>{{ prestageName }}</strong>
                              <v-chip
                                size="x-small"
                                :color="
                                  prestageName === 'Unassigned'
                                    ? 'orange'
                                    : 'blue'
                                "
                                variant="tonal"
                                class="ml-2"
                              >
                                {{ serials.length }} devices
                              </v-chip>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                              <div class="serial-grid">
                                <span
                                  v-for="serial in serials"
                                  :key="serial"
                                  class="text-body-2"
                                >
                                  {{ serial }}
                                </span>
                              </div>
                            </v-expansion-panel-text>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </template>
                    </template>
                  </v-tabs-window-item>

                  <!-- Comparison Tab -->
                  <v-tabs-window-item value="comparison">
                    <template v-if="reportResults.comparison">
                      <v-alert
                        v-if="reportResults.comparison.error"
                        type="error"
                        variant="tonal"
                        class="mt-3"
                      >
                        {{ reportResults.comparison.error }}
                      </v-alert>
                      <v-alert
                        v-else-if="reportResults.comparison.message"
                        type="info"
                        variant="tonal"
                        class="mt-3"
                      >
                        {{ reportResults.comparison.message }}
                      </v-alert>
                      <v-data-table
                        v-else
                        :headers="comparisonHeaders"
                        :items="reportResults.comparison.comparisons || []"
                        :items-per-page="25"
                        class="elevation-2 mt-3"
                        show-expand
                      >
                        <template #[`item.status`]="{ item }">
                          <v-chip
                            :color="comparisonStatusColor(item.status)"
                            size="small"
                            variant="tonal"
                          >
                            {{ item.status }}
                          </v-chip>
                        </template>
                        <template #expanded-row="{ columns, item }">
                          <tr v-if="item.changes">
                            <td :colspan="columns.length" class="pa-4">
                              <v-table density="compact">
                                <thead>
                                  <tr>
                                    <th>Field</th>
                                    <th>Current (Jamf)</th>
                                    <th>Template (GitHub)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr
                                    v-for="(diff, field) in item.changes"
                                    :key="field"
                                  >
                                    <td class="font-weight-medium">
                                      {{ field }}
                                    </td>
                                    <td>
                                      <pre class="setting-value">{{
                                        formatSettingValue(diff.current)
                                      }}</pre>
                                    </td>
                                    <td>
                                      <pre class="setting-value">{{
                                        formatSettingValue(diff.template)
                                      }}</pre>
                                    </td>
                                  </tr>
                                </tbody>
                              </v-table>
                            </td>
                          </tr>
                        </template>
                      </v-data-table>
                    </template>
                  </v-tabs-window-item>

                  <!-- Security Audit Tab -->
                  <v-tabs-window-item value="securityAudit">
                    <template v-if="reportResults.securityAudit">
                      <v-alert
                        v-if="reportResults.securityAudit.error"
                        type="error"
                        variant="tonal"
                        class="mt-3"
                      >
                        {{ reportResults.securityAudit.error }}
                      </v-alert>
                      <v-alert
                        v-else-if="reportResults.securityAudit.sent"
                        type="success"
                        variant="tonal"
                        class="mt-3"
                      >
                        <div class="font-weight-bold mb-1">
                          Security Audit Requested
                        </div>
                        The security audit report will be delivered to your
                        email. Depending on the Jamf instance size, this can
                        take up to 20 minutes.
                      </v-alert>
                    </template>
                  </v-tabs-window-item>
                </v-tabs-window>
              </div>
            </template>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="currentStep > 1 && !loadingReport"
            variant="elevated"
            @click="prevStep"
            class="mr-auto"
            color="rgb(247, 247, 247)"
          >
            Back
          </v-btn>
          <v-btn
            v-if="currentStep === 2"
            variant="elevated"
            @click="nextStep"
            color="primary"
            :disabled="!hasSelectedOption"
          >
            Generate Reports
          </v-btn>
        </v-card-actions>
      </v-sheet>

      <!-- Dialogs -->
      <v-dialog v-model="showFailedCredentialDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="warning"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">
                Credential Check Failed
              </div>
              <div>Verify the Jamf Pro URL and credentials are correct</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showFailedCredentialDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showWarningDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>Please select at least one report type before proceeding</h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="showWarningDialog = false"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
    </v-stepper-content>
  </v-stepper>
</template>

<style scoped>
.setting-value {
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
}
.serial-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 4px;
}
</style>
